import { DateTimeFormatOptions } from 'next-intl';
import { buildNodesFromNetwork } from '@utils/wallet';

// file mimetype
export enum MimeType {
  GIF = 'image/gif',
  JPEG = 'image/jpeg',
  PNG = 'image/png',
  SVG = 'image/svg+xml',
  MP4 = 'video/mp4',
  OGV = 'video/ogg',
  GLB = 'model/gltf-binary',
  MP3 = 'audio/mpeg',
  WAV = 'audio/wav',
  WEB = 'application/x-directory',
  PDF = 'application/pdf',
  VTT = 'text/vtt',
  EIGHTBIDOU = 'image/8bidou8x8',
}

// file extension (used alongside mimetype to generate the "accepts" parameter on upload)
export enum Extension {
  GIF = '.gif',
  JPEG = '.jpg',
  PNG = '.png',
  SVG = '.svg',
  MP4 = '.mp4',
  OGV = '.ogg',
  GLB = '.glb',
  MP3 = '.mp3',
  WAV = '.wav',
  PDF = '.pdf',
  VTT = '.vtt',
}

// Used on format filter
export const MIME_GROUP = [
  {
    value: 'group-image',
    label: 'Image',
    mimes: [
      { mime: MimeType.GIF, label: 'GIF' },
      { mime: MimeType.JPEG, label: 'JPEG' },
      { mime: MimeType.PNG, label: 'PNG' },
      // { mime: MimeType.EIGHTBIDOU, label: '8BIT' },
    ],
  },
  {
    value: 'group-video',
    label: 'Video',
    mimes: [{ mime: MimeType.MP4, label: 'MP4' }],
  },
  {
    value: 'group-audio',
    label: 'Audio',
    mimes: [
      { mime: MimeType.MP3, label: 'MP3' },
      { mime: MimeType.WAV, label: 'WAV' },
    ],
  },
  {
    value: 'group-vector',
    label: 'Vector',
    mimes: [
      { mime: MimeType.SVG, label: 'SVG' },
      { mime: MimeType.PDF, label: 'PDF' },
    ],
  },
  {
    value: 'group-3d',
    label: '3D',
    mimes: [{ mime: MimeType.GLB, label: 'GLB' }],
  },

  {
    value: 'group-code',
    label: 'Code',
    mimes: [{ mime: MimeType.WEB, label: 'WEB' }],
  },
];

// used for uploading to IPFS
export const INFURA_URL = 'https://ipfs.infura.io:5001';

// assets on wallets always use our "item" icon.
export const VERSUM_IPFS_THUMBNAIL_URI = 'ipfs://QmY7npznSASiN61trocXBbYe43iRKKicx2ZtZgQZNJRjtA';

export const MAX_UPLOAD_SIZE_MB = 100;
export const MAX_UPLOAD_SIZE_MB_IMAGE_FALLBACK = 20; // Cloudinary image file size limit

export enum Formats {
  ALL = 'All',
  IMAGE = 'Image',
  VIDEO = 'Video',
  AUDIO = 'Audio',
  VECTOR = 'Vector',
  MODEL_3D = '3D',
  PDF = 'PDF',
  WEB = 'Web',
}

/*
  W3C recommends authors to specify both MIME-types and corresponding extensions in the accept attribute.
  e.g.: accept=".xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  source: https://html.spec.whatwg.org/multipage/input.html#attr-input-accept
*/
export const UPLOAD_ALLOWED_TO_MINT = [
  {
    format: Formats.IMAGE,
    allowed: [
      MimeType.JPEG as string,
      MimeType.PNG as string,
      MimeType.GIF as string,
      Extension.JPEG as string,
      Extension.PNG as string,
      Extension.GIF as string,
    ],
  },
  {
    format: Formats.VIDEO,
    allowed: [MimeType.MP4 as string, Extension.MP4 as string],
  },
  {
    format: Formats.AUDIO,
    allowed: [MimeType.MP3 as string, MimeType.WAV as string, Extension.MP3 as string, Extension.WAV as string],
  },
  {
    format: Formats.VECTOR,
    allowed: [MimeType.SVG as string, Extension.SVG as string],
  },
  {
    format: Formats.MODEL_3D,
    allowed: [MimeType.GLB as string, Extension.GLB as string],
  },
  {
    format: Formats.PDF,
    allowed: [MimeType.PDF as string, Extension.PDF as string],
  },
  {
    format: Formats.WEB,
    allowed: [],
  },
];

export const UPLOAD_ALLOWED_AS_COVER_DYNAMIC = [
  MimeType.JPEG as string,
  MimeType.PNG as string,
  MimeType.MP4 as string,
  Extension.JPEG as string,
  Extension.PNG as string,
  Extension.MP4 as string,
];

export const UPLOAD_ALLOWED_AS_COVER_STATIC = [
  MimeType.JPEG as string,
  MimeType.PNG as string,
  Extension.JPEG as string,
  Extension.PNG as string,
];

export const UPLOAD_ALLOWED_AS_AVATAR = [
  MimeType.JPEG as string,
  MimeType.PNG as string,
  MimeType.GIF as string,
  Extension.JPEG as string,
  Extension.PNG as string,
  Extension.GIF as string,
];

export const REQUIRES_COVER_IMAGE = [
  MimeType.SVG as string,
  MimeType.GLB as string,
  MimeType.MP3 as string,
  MimeType.WAV as string,
  MimeType.PDF as string,
  MimeType.WEB as string,
];

export const VIDEO_MIMETYPES = [MimeType.MP4 as string];

export const AUDIO_MIMETYPES = [MimeType.MP3 as string, MimeType.WAV as string];

export const IMAGE_MIMETYPES = [MimeType.GIF as string, MimeType.JPEG as string, MimeType.PNG as string];

export const IMAGE_VIDEO_MIMETYPES = [...VIDEO_MIMETYPES, ...IMAGE_MIMETYPES];

export enum Platforms {
  FXHASH = 'fxhash',
  HICETNUNC = 'hicetnunc',
  KALAMINT = 'kalamint',
  OBJKTCOM = 'objktcom',
  VERSUM = 'versum',
}

// export const PLATFORMS = [
//   { value: Platforms.FXHASH, label: 'FXHash', icon: 'filter_alt' },
//   { value: Platforms.HICETNUNC, label: 'Hic et nunc', icon: 'filter_alt' },
//   { value: Platforms.KALAMINT, label: 'Kalamint', icon: 'filter_alt' },
//   { value: Platforms.OBJKTCOM, label: 'Objkt.com', icon: 'filter_alt' },
//   { value: Platforms.VERSUM, label: 'Versum', icon: 'filter_alt' },
// ];

// NOT IN USE?
// export const TYPES = [
//   { value: Formats.ALL, label: 'All', icon: 'filter_alt' },
//   { value: Formats.IMAGE, label: 'Image', icon: 'filter_alt' },
//   { value: Formats.VIDEO, label: 'Video', icon: 'filter_alt' },
//   { value: Formats.AUDIO, label: 'Audio', icon: 'filter_alt' },
//   { value: Formats.VECTOR, label: 'Vector', icon: 'filter_alt' },
//   { value: Formats.MODEL_3D, label: '3D', icon: 'filter_alt' },
//   { value: Formats.WEB, label: 'Web', icon: 'filter_alt' },
// ];

export const ROYALTIES = [
  { value: 0, label: 'No Royalties' },
  { value: 50, label: '5%' },
  { value: 100, label: '10%' },
  { value: 150, label: '15%' },
  { value: 200, label: '20%' },
  { value: 250, label: '25%' },
  // { value: 'custom', label: 'Custom Royalties' },
];

export const LICENSES = [
  { value: 'none', label: 'No License' },
  { value: 'CC0-1.0', label: 'Creative Commons Zero v1.0 Universal' },
  { value: 'CC-BY-4.0', label: 'Creative Commons Attribution 4.0 International' },
  { value: 'CC-BY-SA-4.0', label: 'Attribution-ShareAlike 4.0 International' },
  { value: 'custom', label: 'Custom License' },
];

// used when user is not logged in
export const DEFAULT_CURRENCY = 'usd';

// used for number and date formats when user locale is not available
export const DEFAULT_LOCALE = 'en';

export const DATE_OPTIONS: DateTimeFormatOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };

export const NETWORKS = [
  { value: 'mainnet', label: 'Mainnet' },
  { value: 'hangzhounet', label: 'Hangzhounet' },
];

export const RPC_NODES = [
  // ----------------------------------
  // ECAD Labs
  // ----------------------------------
  // { network: 'mainnet', node: 'mainnet.api.tez.ie' }, homeworkpunks told there's an issue with this one.
  { network: 'hangzhounet', node: 'hangzhounet.api.tez.ie' },

  // ----------------------------------
  // SmartPy
  // ----------------------------------
  { network: 'mainnet', node: 'mainnet.smartpy.io' },
  { network: 'hangzhounet', node: 'hangzhounet.smartpy.io' },

  // ----------------------------------
  // Tezos Foundation
  // ----------------------------------
  // { network: 'mainnet', node: 'rpc.tzbeta.net' }, // bors___ resported as dead

  // ----------------------------------
  // LetzBake
  // ----------------------------------
  { network: 'mainnet', node: 'teznode.letzbake.com' },

  // ----------------------------------
  // ateza
  // ----------------------------------
  // { network: 'mainnet', node: 'rpc-mainnet.ateza.io' }, bors___ reported this as broken
  // { network: 'hangzhounet', node: 'rpc-testnet-one.ateza.io' }, fails with a 401 https://rpc-testnet-one.ateza.io/chains/main/blocks/head/context/contracts/KT1PVJP2FbSZrXgKWdhXguMV1HmfrX4dH7o5/script/normalized

  // ----------------------------------
  // teztools
  // ----------------------------------
  { network: 'mainnet', node: 'eu01-node.teztools.net' },

  // { network: 'mainnet', node: 'mainnet-tezos.giganode.io' }, FAILS
  // ----------------------------------
  // tzkt
  // ----------------------------------
  { network: 'mainnet', node: 'rpc.tzkt.io/mainnet' },
  { network: 'handzhounet', node: 'rpc.tzkt.io/hangzhou2net' },
];

const nodes = buildNodesFromNetwork((process.env.LOGGEDOUT_TEZOS_NETWORK as string) || 'mainnet', RPC_NODES);

export const INITIAL_RANDOM_NODE = nodes[Math.floor(nodes.length * Math.random())];

export const FIAT_CURRENCY = [
  { value: 'aed', label: 'United Arab Emirates Dirham' },
  { value: 'ars', label: 'Argentine Peso' },
  { value: 'aud', label: 'Australian Dollar' },
  { value: 'bdt', label: 'Bangladeshi Taka' },
  { value: 'bhd', label: 'Bahraini Dinar' },
  { value: 'bmd', label: 'Bermudian Dollar' },
  { value: 'brl', label: 'Brazil Real' },
  { value: 'cad', label: 'Canadian Dollar' },
  { value: 'chf', label: 'Swiss Franc' },
  { value: 'clp', label: 'Chilean Peso' },
  { value: 'cny', label: 'Chinese Yuan' },
  { value: 'czk', label: 'Czech Koruna' },
  { value: 'dkk', label: 'Danish Krone' },
  { value: 'eur', label: 'Euro' },
  { value: 'gbp', label: 'British Pound Sterling' },
  { value: 'hkd', label: 'Hong Kong Dollar' },
  { value: 'huf', label: 'Hungarian Forint' },
  { value: 'idr', label: 'Indonesian Rupiah' },
  { value: 'ils', label: 'Israeli New Shekel' },
  { value: 'inr', label: 'Indian Rupee' },
  { value: 'jpy', label: 'Japanese Yen' },
  { value: 'krw', label: 'South Korean Won' },
  { value: 'kwd', label: 'Kuwaiti Dinar' },
  { value: 'lkr', label: 'Sri Lankan Rupee' },
  { value: 'mmk', label: 'Burmese Kyat' },
  { value: 'mxn', label: 'Mexican Peso' },
  { value: 'myr', label: 'Malaysian Ringgit' },
  { value: 'ngn', label: 'Nigerian Naira' },
  { value: 'nok', label: 'Norwegian Krone' },
  { value: 'nzd', label: 'New Zealand Dollar' },
  { value: 'php', label: 'Philippine Peso' },
  { value: 'pkr', label: 'Pakistani Rupee' },
  { value: 'pln', label: 'Polish Zloty' },
  { value: 'rub', label: 'Russian Ruble' },
  { value: 'sar', label: 'Saudi Riyal' },
  { value: 'sek', label: 'Swedish Krona' },
  { value: 'sgd', label: 'Singapore Dollar' },
  { value: 'thb', label: 'Thai Baht' },
  { value: 'try', label: 'Turkish Lira' },
  { value: 'twd', label: 'New Taiwan Dollar' },
  { value: 'uah', label: 'Ukrainian hryvnia' },
  { value: 'usd', label: 'US Dollar' },
  { value: 'vef', label: 'Venezuelan bolívar fuerte' },
  { value: 'vnd', label: 'Vietnamese đồng' },
  { value: 'xdr', label: 'IMF Special Drawing Rights' },
  { value: 'zar', label: 'South African Rand' },
];

export enum FEED_TYPE {
  LATEST = 'latest',
  PERSONAL = 'personal',
}

export const ONE_MINUTE_MILLIS = 60 * 1000;

export const ONE_HOUR_MILLIS = 60 * 60 * 1000;

export const ONE_DAY_MILLIS = 24 * 60 * 60 * 1000;

export const MATERIA_MINT_COST = 0.01;

export const BURN_ADDRESS = 'tz1burnburnburnburnburnburnburjAYjjX';

export enum MOBILE_OFFSET {
  LEFT = -1,
  CENTER = 0,
  RIGHT = 1,
}

export const URL_SEPARATOR = ';';
